'use client';
import { Badge } from '@/components';
import useLocale from '@/hooks/useLocale';
import { formatMoney } from '@/utils/helpers';
import interpolate from '@/utils/translations/interpolate';
import { TypeSubFlowV3Translations } from '@/utils/translations/TypeSubFlowV3Translations';
import classNames from 'classnames';
import { FC, useState } from 'react';
import Checkbox from '../../Checkbox/Checkbox';
import {
  SubFlowV3ContextApi,
  SubFlowV3ContextData,
} from '@/components/subflow-v3/context/SubFlowV3Context';
import { useFlags } from 'launchdarkly-react-client-sdk';

type BookBundleAddOnProps = {
  t: TypeSubFlowV3Translations;
  data: SubFlowV3ContextData;
  api: SubFlowV3ContextApi;
  selectedOptionSize?: number;
  drawerStyles?: boolean;
};
const BookBundleAddOn: FC<BookBundleAddOnProps> = ({
  t,
  data,
  api,
  selectedOptionSize,
  drawerStyles,
}) => {
  const locale = useLocale();

  const { 'sub-squad-enable-big-puf': subSquadEnableBigPuf } = useFlags();
  const [isSelected, setIsSelected] = useState(
    data.subscriptionAddOns.length > 0,
  );
  const onClickHandler = () => {
    setIsSelected((selected) => !selected);
    const addOns =
      subSquadEnableBigPuf && selectedOptionSize
        ? data.addOns?.subscription_add_ons.slice(0, selectedOptionSize)
        : data.addOns?.subscription_add_ons.slice(0, 3);
    api.toggleSubscriptionAddOns(addOns);
  };

  const price = data.addOns
    ? formatMoney(
        data.addOns.subscription_add_ons[0].metadata?.price_cents / 100,
        locale,
        true,
      )
    : 0;

  return (
    <button
      data-testid="book-bundle-button"
      className={classNames('w-full', {
        'lg:max-w-[530px]': !drawerStyles,
      })}
      onClick={onClickHandler}
    >
      <div
        data-testid="bundle-container"
        className={classNames(
          'relative rounded border text-left bg-white py-6 px-4 flex flex-col',
          {
            'border-grey-midtone': !isSelected,
            'border-indigo': isSelected,
            'bg-[#F6FBFF]': isSelected,
            'shadow-lg': isSelected,
          },
        )}
      >
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <Badge
            badgeColor="Aqua"
            text={t.paymentOptionsPrepayWithBooksLabel}
          />
        </div>
        <div className="flex sm:flex-col">
          <div
            className={classNames('flex flex-col', {
              'gap-2 lg:gap-4': !drawerStyles,
            })}
          >
            <div className="flex flex-col gap-2 lg:gap-4 justify-between lg:flex-row">
              <Checkbox
                id="addon-checkbox"
                onChange={() => null}
                value={isSelected}
                label={t.paymentOptionsBooksAddOnHeading}
                className="flex items-center"
                checkboxLabelClass={classNames('font-bold !relative', {
                  '!text-h3 lg:!text-h2': !drawerStyles,
                  '!text-d-base': drawerStyles,
                })}
              />
              <p
                className={classNames('text-d-base font-bold pl-9', {
                  'lg:text-h2': !drawerStyles,
                })}
              >
                {interpolate(t.paymentOptionsPrepayWithBooksPricing, {
                  price: `${price}`,
                })}
              </p>
            </div>
            <p
              className={classNames('pl-9', {
                'text-base': drawerStyles,
              })}
            >
              {t.paymentOptionsPrepayWithBooksBody}{' '}
            </p>
          </div>
        </div>
      </div>
    </button>
  );
};

export default BookBundleAddOn;
